import React, { useEffect, useState } from 'react';
import { GoogleLoginRequest, VerificationAccepted } from '@marageti/z4-sdk/lib/auth/Token';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { useApiClient, useQueryParams } from '@marageti/z4-lib';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import ConfirmPhone from './steps/confirm-phone';
import UserIdentification from './steps/user-identification';
import EmailVerification from './steps/email-verification';
import SmsVerification from './steps/sms-verification';
import PasswordVerification from './steps/password-verification';
import debugPromise from '../../utils/debug-promise';
import {
  buildLoginPath,
  getDefaultLoginMethod,
  getLoginStepFromPathname,
  LoginStep,
} from './helpers';
import { useAuthStore } from '../../store';
import LoginLayout from './login-layout';
import useLoginAuth from './use-login-auth';
import useVerificationCooldown from './use-verification-cooldown';

const STEPS_REQUIRING_EMAIL: LoginStep[] = [
  'email_verification',
  'sms_verification',
  'password_verification',
];

const LoginContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { email: emailQueryParam, tripRequestId } = useQueryParams();
  const step = getLoginStepFromPathname(location.pathname);
  const [email, setEmail] = useState(emailQueryParam || '');
  const [isContinuing, setIsContinuing] = useState(false);
  const [googleLoginError, setGoogleLoginError] = useState<ApiError>();
  const [maskedPhoneLast4, setMaskedPhoneLast4] = useState<string | undefined>();
  const [emailReady, setEmailReady] = useState(false);
  const [smsReady, setSmsReady] = useState(false);
  const emailCooldown = useVerificationCooldown();
  const smsCooldown = useVerificationCooldown();

  const isFromTripRequest = !!tripRequestId;
  const { login } = useLoginAuth();
  const { loggedInTraveler } = useAuthStore();
  const resolvedEmail = email || emailQueryParam || '';

  const goToStep = (nextStep: LoginStep, nextEmail = email) => {
    const params = new URLSearchParams(location.search);
    if (nextEmail) {
      params.set('email', nextEmail);
    } else {
      params.delete('email');
    }
    if (nextEmail !== email) {
      setEmail(nextEmail);
    }
    navigate(buildLoginPath(nextStep, params));
  };

  const resetVerificationState = () => {
    emailCooldown.reset();
    smsCooldown.reset();
    setMaskedPhoneLast4(undefined);
    setEmailReady(false);
    setSmsReady(false);
  };

  const handleMaskedPhone = (last4: string | undefined) => {
    setMaskedPhoneLast4(last4 && last4.length === 4 ? last4 : undefined);
  };

  const issueVerification = async (
    channel: 'email' | 'sms',
    forEmail: string,
  ): Promise<void> => {
    if (channel === 'email') {
      emailCooldown.startCooldown();
    } else {
      smsCooldown.startCooldown();
    }
    try {
      const response = await apiClient.authClient.postVerification({
        channel,
        email: forEmail,
      }) as VerificationAccepted;
      handleMaskedPhone(response.maskedPhone);
    } catch (err) {
      console.warn(err);
    } finally {
      if (channel === 'email') {
        setEmailReady(true);
      } else {
        setSmsReady(true);
      }
    }
  };

  useEffect(() => {
    if (!resolvedEmail) {
      return;
    }
    if (step === 'email_verification' && !emailReady) {
      debugPromise(
        'login:bootstrapEmailVerification',
        issueVerification('email', resolvedEmail),
      );
      return;
    }
    if (step === 'sms_verification' && maskedPhoneLast4 && !smsReady) {
      debugPromise(
        'login:bootstrapSmsVerification',
        issueVerification('sms', resolvedEmail),
      );
    }
  }, [step, resolvedEmail, emailReady, smsReady, maskedPhoneLast4]);

  const handleEmailContinue = async (nextEmail: string) => {
    setIsContinuing(true);
    if (nextEmail !== email) {
      resetVerificationState();
    }
    setEmail(nextEmail);

    try {
      const nextStep = await getDefaultLoginMethod(nextEmail);
      if (nextStep === 'email_verification') {
        await issueVerification('email', nextEmail);
      }
      goToStep(nextStep, nextEmail);
    } finally {
      setIsContinuing(false);
    }
  };

  const handleChangeEmail = () => {
    resetVerificationState();
    goToStep('user_identification');
  };

  const handleUsePasswordVerification = () => {
    goToStep('password_verification');
  };

  const handleUseEmailVerification = async () => {
    if (!emailReady || emailCooldown.cooldownSeconds === 0) {
      await issueVerification('email', resolvedEmail);
    }
    goToStep('email_verification');
  };

  const handleUseSmsVerification = async () => {
    if (!maskedPhoneLast4) {
      return;
    }
    if (!smsReady || smsCooldown.cooldownSeconds === 0) {
      await issueVerification('sms', resolvedEmail);
    }
    goToStep('sms_verification');
  };

  const handleGoogleLogin = (loginRequest: GoogleLoginRequest) => {
    setGoogleLoginError(undefined);
    login(loginRequest).catch((e: unknown) => {
      if (
        typeof e === 'object'
        && e !== null
        && 'message' in e
        && typeof (e as ApiError).message === 'string'
      ) {
        setGoogleLoginError(e as ApiError);
        return;
      }
      setGoogleLoginError({ message: e instanceof Error ? e.message : String(e) });
    });
  };

  if (STEPS_REQUIRING_EMAIL.includes(step) && !resolvedEmail) {
    return (
      <Navigate
        to={buildLoginPath('user_identification', new URLSearchParams(location.search))}
        replace
      />
    );
  }

  if (step === 'confirm_phone') {
    if (!loggedInTraveler) {
      return (
        <Navigate
          to={buildLoginPath('user_identification', new URLSearchParams(location.search))}
          replace
        />
      );
    }
    return (
      <LoginLayout>
        <ConfirmPhone />
      </LoginLayout>
    );
  }

  let content: React.ReactNode;
  switch (step) {
    case 'email_verification':
      content = emailReady ? (
        <EmailVerification
          email={resolvedEmail}
          cooldownSeconds={emailCooldown.cooldownSeconds}
          startCooldown={emailCooldown.startCooldown}
          hasSmsPhone={!!maskedPhoneLast4}
          onChangeEmail={handleChangeEmail}
          onUsePasswordVerification={handleUsePasswordVerification}
          onUseSmsVerification={handleUseSmsVerification}
        />
      ) : null;
      break;
    case 'sms_verification':
      if (!maskedPhoneLast4) {
        return (
          <Navigate
            to={buildLoginPath('email_verification', new URLSearchParams(location.search))}
            replace
          />
        );
      }
      content = smsReady ? (
        <SmsVerification
          email={resolvedEmail}
          maskedPhoneLast4={maskedPhoneLast4}
          cooldownSeconds={smsCooldown.cooldownSeconds}
          startCooldown={smsCooldown.startCooldown}
          onChangeEmail={handleChangeEmail}
          onUsePasswordVerification={handleUsePasswordVerification}
          onUseEmailVerification={handleUseEmailVerification}
        />
      ) : null;
      break;
    case 'password_verification':
      content = (
        <PasswordVerification
          email={resolvedEmail}
          onChangeEmail={handleChangeEmail}
        />
      );
      break;
    case 'user_identification':
    default:
      content = (
        <UserIdentification
          email={resolvedEmail}
          isFromTripRequest={isFromTripRequest}
          isSubmitting={isContinuing}
          loginError={googleLoginError}
          onContinue={handleEmailContinue}
          onGoogleLogin={handleGoogleLogin}
        />
      );
  }

  return <LoginLayout>{content}</LoginLayout>;
};

export default LoginContainer;
